<template>
  <HrPageLayout
    title="Performance Improvement Plans (PIP)"
    subtitle="Manage PIPs with weekly employee-manager sign-off"
    icon="📋"
    :breadcrumb-items="[{ label: 'HR Management' }, { label: 'PIP' }]"
  >
    <template #header-actions>
      <button class="hr-btn hr-btn-success" @click="openCreateModal">
        <i class="mdi mdi-plus"></i> Create PIP
      </button>
    </template>

    <div class="layout-content">
      <div class="page-wrapper">
        <!-- Filters and Actions -->
        <div class="filters-section">
          <div class="filters-container">
            <div class="filters-left">
              <div class="filter-group">
                <select v-model="filterStatus" class="filter-select">
                  <option value="">All statuses</option>
                  <option value="Active">Active</option>
                  <option value="Completed">Completed</option>
                  <option value="Terminated">Terminated</option>
                  <option value="Withdrawn">Withdrawn</option>
                </select>
                <button class="btn btn-primary btn-small" @click="loadPips">Apply</button>
              </div>
            </div>
            <div class="filters-right">
              <button class="btn btn-success btn-small" @click="openCreateModal">
                <span class="btn-icon">+</span>
                Create PIP
              </button>
            </div>
          </div>
        </div>

        <!-- PIP Table -->
        <div class="table-section">
          <div class="card">
            <div class="card-content">
              <!-- Loading State -->
              <div v-if="loading" class="loading-state">
                <div class="spinner"></div>
                <p class="loading-text">Loading...</p>
              </div>

              <!-- Empty State -->
              <div v-else-if="pips.length === 0" class="empty-state">
                <div class="empty-icon">📋</div>
                <p class="empty-text">No PIPs found.</p>
              </div>

              <!-- Data Table -->
              <div v-else class="table-container">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Manager</th>
                      <th>Period</th>
                      <th>Status</th>
                      <th class="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in pips" :key="p.id" class="table-row">
                      <td>{{ p.employee_name }}</td>
                      <td>{{ p.manager_name || '—' }}</td>
                      <td>{{ formatDate(p.start_date) }} – {{ formatDate(p.end_date) }}</td>
                      <td>
                        <span class="status-badge" :class="getStatusClass(p.status)">
                          {{ p.status }}
                        </span>
                      </td>
                      <td class="text-right">
                        <button class="btn btn-outline btn-small" @click="viewPip(p)">
                          View & Sign-off
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- Modals -->
        <!-- Create PIP Modal -->
        <ModalWrapper ref="createModal" title="Create PIP">
          <template #body>
            <div class="modal-form">
              <div class="form-grid">
                <div class="form-group">
                  <label class="form-label">
                    Employee (EmpNo / ID / Email)
                    <span class="required">*</span>
                  </label>
                  <input
                    v-model="createForm.employeeIdentifier"
                    type="text"
                    class="form-input"
                    placeholder="Enter identifier"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">
                    Manager (EmpNo / ID / Email)
                    <span class="required">*</span>
                  </label>
                  <input
                    v-model="createForm.managerIdentifier"
                    type="text"
                    class="form-input"
                    placeholder="Enter identifier"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">
                    Start Date
                    <span class="required">*</span>
                  </label>
                  <input
                    v-model="createForm.startDate"
                    type="date"
                    class="form-input"
                  />
                </div>
                <div class="form-group">
                  <label class="form-label">
                    End Date
                    <span class="required">*</span>
                  </label>
                  <input
                    v-model="createForm.endDate"
                    type="date"
                    class="form-input"
                  />
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">Reason / Goals</label>
                <textarea
                  v-model="createForm.reason"
                  class="form-textarea"
                  rows="3"
                  placeholder="Improvement goals and expectations"
                ></textarea>
              </div>
            </div>
          </template>
          <template #footer>
            <button class="btn btn-secondary" @click="$refs.createModal.close()">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              @click="createPip"
              :disabled="creating"
            >
              <span v-if="creating" class="btn-spinner"></span>
              {{ creating ? 'Creating...' : 'Create PIP' }}
            </button>
          </template>
        </ModalWrapper>

        <!-- View PIP Modal -->
        <ModalWrapper ref="viewModal" :title="'PIP: ' + (selectedPip?.employee_name || '')" size="large">
          <template #body>
            <div v-if="loadingDetail" class="loading-state">
              <div class="spinner small"></div>
            </div>
            <div v-else-if="selectedPip" class="pip-details">
              <!-- PIP Info -->
              <div class="pip-info">
                <div class="info-item">
                  <strong>Period:</strong>
                  {{ formatDate(selectedPip.start_date) }} – {{ formatDate(selectedPip.end_date) }}
                  <span class="status-badge" :class="getStatusClass(selectedPip.status)">
                    {{ selectedPip.status }}
                  </span>
                </div>
                <div v-if="selectedPip.reason" class="info-item">
                  <strong>Goals:</strong>
                  <pre class="goals-text">{{ selectedPip.reason }}</pre>
                </div>
              </div>

              <!-- Weekly Sign-offs -->
              <div class="signoffs-section">
                <h3 class="section-title">Weekly Sign-offs</h3>
                <div class="signoffs-table">
                  <table class="compact-table">
                    <thead>
                      <tr>
                        <th>Week</th>
                        <th>Progress</th>
                        <th>Employee</th>
                        <th>Manager</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="s in signoffWeeks" :key="s.week_start">
                        <td>{{ formatDate(s.week_start) }} – {{ formatDate(s.week_end) }}</td>
                        <td>
                          <span class="progress-text">
                            {{ (s.progress_notes || '').slice(0, 40) }}
                          </span>
                        </td>
                        <td>
                          <span class="signature-status">
                            {{ s.employee_signed_at ? 'Signed ' + formatDateTime(s.employee_signed_at) : '—' }}
                          </span>
                        </td>
                        <td>
                          <span class="signature-status">
                            {{ s.manager_signed_at ? 'Signed ' + formatDateTime(s.manager_signed_at) : '—' }}
                          </span>
                        </td>
                        <td>
                          <button
                            class="btn btn-outline btn-very-small"
                            @click="openSignoffModal(s)"
                            :disabled="selectedPip.status !== 'Active'"
                          >
                            {{ getSignoffLabel(s) }}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Actions for Active PIPs -->
              <div v-if="selectedPip.status === 'Active'" class="pip-actions">
                <button
                  class="btn btn-outline btn-small"
                  @click="updatePipStatus('Completed')"
                >
                  Mark Completed
                </button>
                <button
                  class="btn btn-danger btn-small"
                  @click="updatePipStatus('Terminated')"
                >
                  Terminate
                </button>
              </div>
            </div>
          </template>
          <template #footer>
            <button class="btn btn-secondary" @click="$refs.viewModal.close()">
              Close
            </button>
          </template>
        </ModalWrapper>

        <!-- Signoff Modal -->
        <ModalWrapper ref="signoffModal" :title="'Weekly Sign-off (' + formatDate(signoffForm.week_start) + ')'">
          <template #body>
            <div class="signoff-form">
              <div class="form-group">
                <label class="form-label">Progress Notes</label>
                <textarea
                  v-model="signoffForm.progressNotes"
                  class="form-textarea"
                  rows="2"
                ></textarea>
              </div>
              <div class="form-group">
                <label class="form-label">Comments ({{ signoffAs }})</label>
                <textarea
                  v-model="signoffForm.comments"
                  class="form-textarea"
                  rows="2"
                  :placeholder="signoffAs === 'employee' ? 'Employee feedback...' : 'Manager feedback...'"
                ></textarea>
              </div>
            </div>
          </template>
          <template #footer>
            <button class="btn btn-secondary" @click="$refs.signoffModal.close()">
              Cancel
            </button>
            <button
              class="btn btn-primary"
              @click="submitSignoff"
              :disabled="signing"
            >
              <span v-if="signing" class="btn-spinner"></span>
              {{ signing ? 'Signing...' : 'Sign' }}
            </button>
          </template>
        </ModalWrapper>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "./HrPageLayout.vue";
import ModalWrapper from "./ModalWrapper.vue";

export default {
  name: "PipManagement",
  components: { HrPageLayout, ModalWrapper },
  data() {
    return {
      loading: false,
      loadingDetail: false,
      creating: false,
      signing: false,
      filterStatus: '',
      pips: [],
      selectedPip: null,
      signoffs: [],
      signoffWeeks: [],
      createForm: {
        employeeIdentifier: '',
        managerIdentifier: '',
        startDate: '',
        endDate: '',
        reason: ''
      },
      signoffForm: {
        week_start: '',
        week_end: '',
        progressNotes: '',
        comments: ''
      },
      signoffAs: 'employee',
      currentUserNationalId: ''
    };
  },
  mounted() {
    this.loadPips();
    this.loadCurrentUser();
  },
  methods: {
    openCreateModal() {
      const today = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 90);
      
      this.createForm = {
        employeeIdentifier: '',
        managerIdentifier: '',
        startDate: today.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
        reason: ''
      };
      this.$refs.createModal.open();
    },
    
    async loadCurrentUser() {
      try {
        const d = new FormData();
        d.append("function", "getEmployeeFromToken");
        const res = await execute(d, authUrl);
        if (res?.data?.data?.nationalId) {
          this.currentUserNationalId = res.data.data.nationalId;
        }
      } catch (e) {
        console.error("Failed to load current user:", e);
      }
    },
    
    async loadPips() {
      this.loading = true;
      try {
        const data = new FormData();
        data.append("function", "getPips");
        if (this.filterStatus) {
          data.append("status", this.filterStatus);
        }
        const res = await execute(data, authUrl);
        this.pips = res?.data?.success ? (res.data.data?.pips ?? []) : [];
      } catch (e) {
        console.error("Failed to load PIPs:", e);
        this.pips = [];
      } finally {
        this.loading = false;
      }
    },
    
    async createPip() {
      if (!this.createForm.employeeIdentifier || 
          !this.createForm.managerIdentifier ||
          !this.createForm.startDate || 
          !this.createForm.endDate) {
        alert("Please fill all required fields");
        return;
      }
      
      this.creating = true;
      try {
        const emp = await this.resolveEmployee(this.createForm.employeeIdentifier);
        const mgr = await this.resolveEmployee(this.createForm.managerIdentifier);
        
        if (!emp || !mgr) {
          throw new Error("Employee or manager not found");
        }

        const data = new FormData();
        data.append("function", "createPip");
        data.append("employeeEmpNo", emp.empNo);
        data.append("employeeNationalId", emp.nationalId);
        data.append("employeeName", `${emp.firstName} ${emp.lastName}`);
        data.append("managerEmpNo", mgr.empNo);
        data.append("managerNationalId", mgr.nationalId);
        data.append("managerName", `${mgr.firstName} ${mgr.lastName}`);
        data.append("startDate", this.createForm.startDate);
        data.append("endDate", this.createForm.endDate);
        data.append("reason", this.createForm.reason || '');
        data.append("goals", this.createForm.reason || '');
        
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          alert("PIP created successfully");
          this.$refs.createModal.close();
          this.loadPips();
        } else {
          throw new Error(res?.data?.message || "Failed to create PIP");
        }
      } catch (e) {
        alert(e.message);
      } finally {
        this.creating = false;
      }
    },
    
    async resolveEmployee(identifier) {
      try {
        const d = new FormData();
        d.append("function", "resolveEmployeeIdentifier");
        d.append("identifier", identifier);
        const res = await execute(d, authUrl);
        return res?.data?.data ?? null;
      } catch (e) {
        console.error("Failed to resolve employee:", e);
        return null;
      }
    },
    
    async viewPip(p) {
      this.selectedPip = p;
      this.loadingDetail = true;
      try {
        const data = new FormData();
        data.append("function", "getPipDetail");
        data.append("id", p.id);
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          this.selectedPip = res.data.data?.pip ?? p;
          this.signoffs = res.data.data?.signoffs ?? [];
          await this.loadWeekSlots();
        }
      } catch (e) {
        alert("Failed to load PIP details");
        console.error(e);
      } finally {
        this.loadingDetail = false;
        this.$refs.viewModal.open();
      }
    },
    
    async loadWeekSlots() {
      if (!this.selectedPip) return;
      
      try {
        const data = new FormData();
        data.append("function", "generatePipWeekSlots");
        data.append("pipId", this.selectedPip.id);
        const res = await execute(data, authUrl);
        const slots = res?.data?.data?.slots ?? [];
        
        this.signoffWeeks = slots.map(s => {
          const existing = this.signoffs.find(
            x => (x.week_start_date || x.week_start) === s.week_start
          );
          return existing ? { ...s, ...existing } : s;
        });
      } catch (e) {
        console.error("Failed to load week slots:", e);
      }
    },
    
    getSignoffLabel(s) {
      if (s.status === 'Both_Signed') return 'View';
      if (s.employee_signed_at && !s.manager_signed_at) return 'Manager Sign';
      return 'Sign';
    },
    
    openSignoffModal(s) {
      this.signoffForm = {
        week_start: s.week_start,
        week_end: s.week_end,
        progressNotes: s.progress_notes || '',
        comments: s.employee_signed_at && !s.manager_signed_at 
          ? (s.manager_comments || '') 
          : (s.employee_comments || '')
      };
      
      const isManager = this.selectedPip?.manager_national_id === this.currentUserNationalId;
      this.signoffAs = isManager ? 'manager' : 'employee';
      this.$refs.signoffModal.open();
    },
    
    async submitSignoff() {
      this.signing = true;
      try {
        const data = new FormData();
        data.append("function", "addOrUpdatePipWeeklySignoff");
        data.append("pipId", this.selectedPip.id);
        data.append("weekStartDate", this.signoffForm.week_start);
        data.append("progressNotes", this.signoffForm.progressNotes || '');
        data.append("signedAs", this.signoffAs);
        
        if (this.signoffAs === 'employee') {
          data.append("employeeComments", this.signoffForm.comments || '');
        } else {
          data.append("managerComments", this.signoffForm.comments || '');
        }

        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          alert("Sign-off saved successfully");
          this.$refs.signoffModal.close();
          this.viewPip(this.selectedPip);
        } else {
          throw new Error(res?.data?.message || "Failed to save sign-off");
        }
      } catch (e) {
        alert(e.message);
      } finally {
        this.signing = false;
      }
    },
    
    async updatePipStatus(status) {
      if (!this.selectedPip) return;
      
      if (!confirm(`Are you sure you want to change status to "${status}"?`)) {
        return;
      }
      
      try {
        const data = new FormData();
        data.append("function", "updatePipStatus");
        data.append("pipId", this.selectedPip.id);
        data.append("status", status);
        
        const res = await execute(data, authUrl);
        if (res?.data?.success) {
          alert("Status updated successfully");
          this.$refs.viewModal.close();
          this.loadPips();
        } else {
          throw new Error(res?.data?.message || "Failed to update status");
        }
      } catch (e) {
        alert(e.message);
      }
    },
    
    formatDate(d) {
      if (!d) return '—';
      const date = new Date(d);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    formatDateTime(d) {
      if (!d) return '—';
      const date = new Date(d);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getStatusClass(status) {
      const statusClasses = {
        'Active': 'status-active',
        'Completed': 'status-completed',
        'Terminated': 'status-terminated',
        'Withdrawn': 'status-withdrawn'
      };
      return statusClasses[status] || 'status-default';
    },
    
    goto(page) {
      this.$router.push({ name: page });
    }
  }
};
</script>

<style scoped>
/* Layout */
.pip-management {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.layout-content {
  padding: 20px;
}

.page-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
}

.breadcrumb-link {
  color: #0066cc;
  text-decoration: none;
  cursor: pointer;
  background: none;
  border: none;
  font-size: 14px;
  padding: 0;
}

.breadcrumb-link:hover {
  text-decoration: underline;
}

.breadcrumb-separator {
  color: #999;
}

.breadcrumb-current {
  color: #333;
  font-weight: 500;
}

/* Filters */
.filters-section {
  margin-bottom: 24px;
}

.filters-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #333;
  min-width: 150px;
}

.filter-select:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

/* Buttons */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-small {
  padding: 6px 12px;
  font-size: 13px;
}

.btn-very-small {
  padding: 4px 8px;
  font-size: 12px;
}

.btn-primary {
  background-color: #0066cc;
  color: white;
  border-color: #0066cc;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0052a3;
}

.btn-success {
  background-color: #00a854;
  color: white;
  border-color: #00a854;
}

.btn-success:hover:not(:disabled) {
  background-color: #008f46;
}

.btn-secondary {
  background-color: #cd1426;
  color: #333;
  border-color: #ddd;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #e0e0e0;
}

.btn-outline {
  background-color: transparent;
  color: #0066cc;
  border-color: #0066cc;
}

.btn-outline:hover:not(:disabled) {
  background-color: rgba(0, 102, 204, 0.1);
}

.btn-danger {
  background-color: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn-icon {
  font-size: 16px;
}

.btn-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  margin-right: 6px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Card */
.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-content {
  padding: 0;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 102, 204, 0.1);
  border-top-color: #0066cc;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.small {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

.loading-text {
  margin-top: 12px;
  color: #666;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 20px;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  margin: 0;
}

/* Table */
.table-section {
  margin-top: 24px;
}

.table-container {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
}

.data-table th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  color: #333;
  font-size: 14px;
}

.data-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  font-size: 14px;
  color: #333;
}

.data-table tbody tr {
  transition: background-color 0.2s;
}

.data-table tbody tr:hover {
  background-color: rgba(0, 102, 204, 0.05);
}

.text-right {
  text-align: right;
}

/* Status Badges */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-active {
  background-color: #d4edda;
  color: #155724;
}

.status-completed {
  background-color: #d1ecf1;
  color: #0c5460;
}

.status-terminated {
  background-color: #f8d7da;
  color: #721c24;
}

.status-withdrawn {
  background-color: #fff3cd;
  color: #856404;
}

.status-default {
  background-color: #e9ecef;
  color: #495057;
}

/* Modal Form Styling */
.modal-form {
  padding: 8px 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.required {
  color: #dc3545;
}

.form-input,
.form-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  color: #333;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus {
  outline: none;
  border-color: #0066cc;
  box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
}

.form-textarea {
  min-height: 80px;
  resize: vertical;
}

/* PIP Details */
.pip-details {
  padding: 8px 0;
}

.pip-info {
  margin-bottom: 24px;
}

.info-item {
  margin-bottom: 12px;
  color: #333;
  font-size: 14px;
}

.info-item strong {
  margin-right: 8px;
}

.goals-text {
  margin: 8px 0 0 0;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 200px;
  overflow-y: auto;
}

/* Signoffs Section */
.signoffs-section {
  margin: 24px 0;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px 0;
  color: #333;
}

.signoffs-table {
  overflow-x: auto;
}

.compact-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.compact-table th {
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #666;
  text-align: left;
}

.compact-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #e9ecef;
  color: #333;
}

.progress-text {
  color: #666;
  font-style: italic;
}

.signature-status {
  color: #666;
  font-size: 12px;
}

/* PIP Actions */
.pip-actions {
  display: flex;
  gap: 8px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
}

/* Signoff Form */
.signoff-form {
  padding: 8px 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .layout-content {
    padding: 12px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .filters-container {
    flex-direction: column;
    align-items: stretch;
  }
  
  .filters-left,
  .filters-right {
    width: 100%;
  }
  
  .filter-group {
    width: 100%;
  }
  
  .filter-select {
    flex: 1;
  }
  
  .form-grid {
    grid-template-columns: 1fr;
  }
  
  .data-table th,
  .data-table td {
    padding: 8px 12px;
  }
}
</style>